// Validación comercial pre-facturación (solo para Alfonso).
// Muestra un popup al iniciar sesión con los eventos ya celebrados pendientes de
// validar, y un botón flotante rojo con contador mientras queden pendientes.
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVistaEmpleado } from '../context/VistaEmpleadoContext';
import { validacionComercialAPI } from '../api/api';
import { X, CheckCircle2, ExternalLink, ClipboardCheck, AlertTriangle } from 'lucide-react';

const ALFONSO_ID = '03cd42c0-3259-489e-b934-50e883d87b68';
const MANU_ID = 'c579306f-0572-49e5-9488-9adc96762548'; // empleado Manu (login de empleado)
const ADMIN_ID = 'admin-001'; // usuario admin de direccion
// Validadores que ven el popup: Alfonso, Manu y el admin (decision de direccion, 07-oct-2026).
const VALIDADORES = [ALFONSO_ID, MANU_ID, ADMIN_ID];

const ValidacionComercialAlfonso = () => {
  const { user } = useAuth();
  const { simulando } = useVistaEmpleado();
  const enEmails = useLocation().pathname.startsWith('/emails');
  // En la vista simulada de otro empleado no se enseña: es un aviso personal del validador.
  const esValidador = !simulando && VALIDADORES.includes(user?.id);

  const [pendientes, setPendientes] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [validando, setValidando] = useState(null);

  const cargar = useCallback(async () => {
    if (!esValidador) return;
    try {
      const res = await validacionComercialAPI.getPendientes();
      const items = res.data?.pendientes || [];
      setPendientes(items);
      // Popup automático una vez por sesión de navegador si hay pendientes.
      if (items.length > 0 && !sessionStorage.getItem('validacion_popup_visto')) {
        setAbierto(true);
        sessionStorage.setItem('validacion_popup_visto', '1');
      }
    } catch (e) {
      // silencioso: no molestar si falla la carga
    }
  }, [esValidador]);

  useEffect(() => {
    if (!esValidador) return;
    cargar();
    const iv = setInterval(cargar, 5 * 60 * 1000); // refresco cada 5 min
    return () => clearInterval(iv);
  }, [esValidador, cargar]);

  const keyDe = (p) => `${p.id}-${p.jornada_id || 'p'}`;

  const handleValidar = async (p) => {
    const k = keyDe(p);
    setValidando(k);
    try {
      await validacionComercialAPI.validar(p.id, p.jornada_id);
      setPendientes((prev) => prev.filter((x) => keyDe(x) !== k));
    } catch (e) {
      // silencioso
    } finally {
      setValidando(null);
    }
  };

  const abrirDetalle = (id) => {
    window.open(`/presupuestos/${id}`, '_blank', 'noopener');
  };

  if (!esValidador) return null;
  const count = pendientes.length;

  return (
    <>
      {/* Botón flotante rojo con contador (encima del botón del asistente IA) */}
      {count > 0 && !abierto && (
        <button
          onClick={() => setAbierto(true)}
          // En el móvil, dentro de IAmail, sube por encima de la barra inferior del correo abierto
          className={`fixed right-6 bg-red-500 hover:bg-red-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all z-50 group ${
            enEmails ? 'bottom-24 md:bottom-6' : 'bottom-6'
          }`}
          title="Eventos pendientes de validar"
        >
          <ClipboardCheck className="w-6 h-6" />
          <span className="absolute -top-2 -right-2 bg-white text-red-600 text-[11px] min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center font-bold border border-red-200 shadow">
            {count > 99 ? '99+' : count}
          </span>
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            {count} evento{count === 1 ? '' : 's'} por validar
          </span>
        </button>
      )}

      {/* Popup / modal con la lista */}
      {abierto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-red-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h2 className="text-lg font-bold text-gray-900">Eventos pendientes de validar</h2>
                {count > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{count}</span>
                )}
              </div>
              <button onClick={() => setAbierto(false)} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-3 text-sm text-gray-600 bg-gray-50 border-b border-gray-100">
              Revisa cada evento ya celebrado antes de facturar. Si hubo cambios de última hora
              (horas, bebidas, camareros), pulsa <strong>Revisar</strong> para abrir el presupuesto
              en otra pestaña y ajustarlo; si está todo correcto, pulsa <strong>Validar</strong>.
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {count === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500" />
                  No hay eventos pendientes de validar. ¡Todo al día!
                </div>
              ) : (
                pendientes.map((p) => (
                  <div key={keyDe(p)} className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">
                        #{p.numero} · {p.cliente_nombre}
                        {p.es_jornada && <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 align-middle">Multievento · {p.jornada_label}</span>}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {p.fechas_texto || p.fecha_evento}
                        {(p.personas_texto || p.num_personas) ? ` · ${p.personas_texto || p.num_personas} pax` : ''}
                        {p.importe_total ? ` · €${Number(p.importe_total).toLocaleString('es-ES')}` : ''}
                      </p>
                      {p.lugar_evento && <p className="text-xs text-gray-400 truncate mt-0.5">{p.lugar_evento}</p>}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        onClick={() => abrirDetalle(p.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Revisar
                      </button>
                      <button
                        onClick={() => handleValidar(p)}
                        disabled={validando === keyDe(p)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-duetto-primary text-white hover:opacity-90 disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> {validando === keyDe(p) ? '...' : 'Validar'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setAbierto(false)}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ValidacionComercialAlfonso;
